import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import { updatePluginToolbar } from "../../../common/actions";
import { downloadInteractive, fetchInteractiveCatalog } from "./interactiveItemService";
import "./_interactiveItem.scss";

/* eslint-disable react/prop-types */

/**
 * İnteraktif öğenin Düzenle ekranı.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * NEDEN BU EKRAN VAR
 * ──────────────────────────────────────────────────────────────────────────
 * Üretilen aktivitenin KODU modele ait ve dokunulmaz, ama İÇERİĞİ yazarın.
 * Bir kartın yazısındaki hata, yanlış bir cevap anahtarı, fazlalık bir çift —
 * bunlar için yeniden üretim yaptırmak iki şeyi birden yapar: para harcatır
 * ve yazarın beğendiği aktiviteyi yok eder, çünkü ikinci üretim ilkinden
 * başka bir şey döndürür.
 *
 * Burada düzenlenen `state.item`. Değişiklik kutunun state'ine yazılır,
 * plugin yeni veriyi iframe'e yollar, widget kendini yeniden çizer.
 * Milisaniyeler, sıfır kredi, model yok.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * NEDEN 23 FORM DEĞİL, 10
 * ──────────────────────────────────────────────────────────────────────────
 * Formu TİP değil ŞEKİL belirler. Hafıza oyunu da iki sütun bağlama da
 * "çiftler"dir; hotspot avı da etiketleme de "görsel üzerinde noktalar".
 * Backend her şekil için alan listesini veriyor (`/interactive/types` →
 * shapes), bu bileşen o listeden form çiziyor. Katalogdaki 24. tip var olan
 * bir şekli kullanıyorsa burada yapılacak iş sıfır.
 */
export default function InteractiveItemEditor({ boxId, state, onClose }) {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(null);
  const [error, setError] = useState(null);
  // Düzenleme kopya üzerinde yürür: her tuş vuruşunu state'e yazmak, her
  // harf için bir geri-al adımı ve bir otomatik kayıt demek.
  const [draft, setDraft] = useState(() => clone(state?.item) || {});
  const [dirty, setDirty] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const shape = state?.shape;

  useEffect(() => {
    let cancelled = false;
    fetchInteractiveCatalog()
      .then((catalog) => {
        if (cancelled) return;
        const found = (catalog?.shapes || []).find((s) => s.shape === shape);
        setFields(found?.fields || []);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [shape]);

  const apply = useCallback(() => {
    // Yazılan yer `pluginToolbarsById[boxId].state.item` — plugin içeriğinin
    // yetkili kaynağı. Hem editör hem visor `getRenderTemplate(toolbar.state,
    // …)` çağırdığı için okunan kopya budur; `boxesById[boxId].state` eklenme
    // anındaki haliyle kalır ve onu hiçbir şey render etmez (bkz. CLAUDE.md,
    // CourseJsonModel). İkinci bir yazma yolu açmak, iki kopyanın hangisinin
    // doğru olduğunu belirsizleştirmek olurdu.
    dispatch(updatePluginToolbar(boxId, "main", "state", "item", draft, [], null));
    setDirty(false);
    onClose?.();
  }, [dispatch, boxId, draft, onClose]);

  /**
   * Aktiviteyi tek bir HTML dosyası olarak indir.
   *
   * Gönderilen veri `draft` — yani EKRANDA DURAN hâli, state'e yazılmış hâli
   * değil. Yazar bir kartın yazısını düzeltip "Uygula"ya basmadan indirirse,
   * indirdiği dosyada gördüğü yazı olmalı; aksi hâli açıklanamaz.
   */
  const download = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      await downloadInteractive({
        typeId: state?.typeId,
        sourceBlobName: state?.sourceBlobName,
        // Dil kutunun state'inde tutulmuyor; sunucu tr-TR'ye düşer. Dosyada
        // yalnızca <html lang> ve api.lang'i etkiliyor, içeriği değil.
        item: draft,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  }, [state, draft]);

  const setRow = useCallback((path, index, key, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      const list = Array.isArray(next[path]) ? next[path].slice() : [];
      list[index] = { ...(list[index] || {}), [key]: value };
      next[path] = list;
      return next;
    });
    setDirty(true);
  }, []);

  const addRow = useCallback((path) => {
    setDraft((prev) => {
      const next = { ...prev };
      next[path] = [...(Array.isArray(next[path]) ? next[path] : []), {}];
      return next;
    });
    setDirty(true);
  }, []);

  const removeRow = useCallback((path, index) => {
    setDraft((prev) => {
      const next = { ...prev };
      next[path] = (Array.isArray(next[path]) ? next[path] : []).filter((_, i) => i !== index);
      return next;
    });
    setDirty(true);
  }, []);

  const title = state?.typeLabel || i18n.t("InteractiveItem.PluginName");

  // PORTAL — gövdeye, kutunun içine değil.
  //
  // Bu ekran seçili kutunun içinden açılıyor ve editör kutuları `transform`
  // taşıyor (konum, döndürme, canvas ölçeği). Transform'lu bir ata,
  // `position: fixed`'in referansını kendisi yapar: overlay ekranı değil o
  // kutuyu kaplar, yani sahnede 200 piksellik bir alana sıkışır ve formun her
  // kelimesi alt alta düşer. Bileşen panelinde tam olarak bu oldu.
  return createPortal(
    <div className="ii-editor" onClick={(e) => e.stopPropagation()}>
      <div className="ii-editor__panel">
        <header className="ii-editor__head">
          <div>
            <h2>{title}</h2>
            <p>
              {i18n.t("InteractiveItem.EditorHint", {
                defaultValue: "İçeriği buradan değiştir — yeniden üretim yok, kredi harcanmaz.",
              })}
            </p>
          </div>
          <button type="button" className="ii-editor__x" onClick={onClose}>
            ✕
          </button>
        </header>

        <div className="ii-editor__body">
          {error && <div className="ii-editor__error">{error}</div>}
          {!fields && !error && (
            <div className="ii-editor__loading">{i18n.t("loading") || "Yükleniyor…"}</div>
          )}
          {fields &&
            fields.map((field) => (
              <FieldGroup
                key={field.path}
                field={field}
                rows={Array.isArray(draft[field.path]) ? draft[field.path] : []}
                draft={draft}
                onChange={setRow}
                onAdd={addRow}
                onRemove={removeRow}
              />
            ))}
        </div>

        <footer className="ii-editor__foot">
          {/* Sola yaslı: "İptal/Uygula" bu ekranın kararı, indirme ise ondan
              bağımsız bir eylem — aralarına karışırsa yazar hangisinin
              kaydettiğini düşünmek zorunda kalır. */}
          <button
            type="button"
            className="ii-btn ii-editor__download"
            onClick={download}
            // Kaynağı olmayan kutular bu özellikten önce üretilmiş olanlar;
            // onlardan dosya kurulamıyor.
            disabled={downloading || !state?.sourceBlobName}
            title={
              state?.sourceBlobName
                ? i18n.t("InteractiveItem.DownloadHint", {
                    defaultValue:
                      "Aktiviteyi tek bir HTML dosyası olarak indir — başka bir uygulamada kullanabilirsin.",
                  })
                : i18n.t("InteractiveItem.DownloadUnavailable", {
                    defaultValue: "Bu öğe indirilemiyor (kaynağı yok).",
                  })
            }
          >
            {downloading
              ? i18n.t("InteractiveItem.Downloading", { defaultValue: "Hazırlanıyor…" })
              : i18n.t("InteractiveItem.Download", { defaultValue: "HTML indir" })}
          </button>
          <button type="button" className="ii-btn" onClick={onClose}>
            {i18n.t("cancel") || "Vazgeç"}
          </button>
          <button
            type="button"
            className="ii-btn ii-btn--primary"
            onClick={apply}
            disabled={!dirty}
          >
            {i18n.t("InteractiveItem.Apply", { defaultValue: "Uygula" })}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

function FieldGroup({ field, rows, draft, onChange, onAdd, onRemove }) {
  // kind:'choice' seçeneklerini ITEM'ın başka bir dizisinden okur
  // ('buckets.name' → draft.buckets[*].name). Kategorileri elle yazdırmak,
  // bir öğeyi var olmayan bir kovaya atamanın en kolay yolu olurdu.
  const optionsFor = useMemo(
    () => (spec) => {
      if (!spec) return [];
      const [path, key] = spec.split(".");
      return (Array.isArray(draft[path]) ? draft[path] : [])
        .map((row) => row?.[key])
        .filter(Boolean);
    },
    [draft]
  );

  const atMin = rows.length <= (field.min ?? 0);
  const atMax = rows.length >= (field.max ?? 99);
  // Yerleşimi ŞEKİL belirler (backend'den gelir), tip değil — şemanın
  // kendisinde olduğu gibi. Eski hâlinde her grup tek bir dikey akıştı: kart
  // destesi de sıralı adımlar da aynı gri şeritler listesi olarak geliyordu
  // ve yazar ekranda düzenlediği şeyi tanıyamıyordu.
  const isGrid = field.layout === "grid";

  const removeButton = (index) =>
    field.addable && (
      <button
        type="button"
        className={isGrid ? "ii-card__x" : "ii-row__x"}
        onClick={() => onRemove(field.path, index)}
        // Asgarinin altına inen bir aktivite oynanamaz hale gelir — iki
        // kartlı bir hafıza oyunu bir aktivite değildir.
        disabled={atMin}
        title={
          atMin
            ? i18n.t("InteractiveItem.MinRows", {
                defaultValue: `En az ${field.min} tane olmalı`,
              })
            : i18n.t("delete") || "Sil"
        }
      >
        ✕
      </button>
    );

  const fieldsOf = (row, index) =>
    field.of.map((spec) => (
      <label
        className={
          // Uzun metin ve dizi alanları bir kartın içinde tam genişlik ister;
          // yan yana sıkıştırıldıklarında iki kelime görünüyor.
          spec.kind === "longtext" ? "ii-field ii-field--wide" : "ii-field"
        }
        key={spec.key}
      >
        <span>{spec.label}</span>
        <Input
          spec={spec}
          value={row?.[spec.key]}
          options={optionsFor(spec.optionsFrom)}
          onChange={(value) => onChange(field.path, index, spec.key, value)}
        />
      </label>
    ));

  return (
    <section className="ii-group">
      <div className="ii-group__head">
        <h3>{field.label}</h3>
        <span className="ii-group__count">
          {rows.length}
          {field.max ? ` / ${field.max}` : ""}
        </span>
      </div>

      {isGrid ? (
        // KART düzeni: her satır kendi başına duran bir kutu, soldan sağa.
        <div className="ii-cards">
          {rows.map((row, index) => (
            <div className="ii-card" key={index}>
              <div className="ii-card__head">
                <span className="ii-card__n">{index + 1}</span>
                {removeButton(index)}
              </div>
              <div className="ii-card__fields">{fieldsOf(row, index)}</div>
            </div>
          ))}
        </div>
      ) : (
        // ŞERİT düzeni: sıra anlamlıysa ya da iki taraf yan yana okunacaksa.
        rows.map((row, index) => (
          <div className="ii-row" key={index}>
            <span className="ii-row__n">{index + 1}</span>
            <div className="ii-row__fields">{fieldsOf(row, index)}</div>
            {removeButton(index)}
          </div>
        ))
      )}

      {field.addable && (
        <button
          type="button"
          className="ii-group__add"
          onClick={() => onAdd(field.path)}
          disabled={atMax}
        >
          + {i18n.t("InteractiveItem.AddRow", { defaultValue: "Ekle" })}
        </button>
      )}
    </section>
  );
}

function Input({ spec, value, options, onChange }) {
  if (spec.kind === "bool") {
    return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
  }
  if (spec.kind === "number") {
    return (
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
    );
  }
  if (spec.kind === "choice") {
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  if (spec.kind === "longtext") {
    return <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  // Dizi alanları (şıklar, cevaplar) virgülle yazılır: tek bir şık için ayrı
  // bir alt-form, dört şıklı bir soruyu düzenlemeyi dört kata çıkarırdı.
  if (Array.isArray(value)) {
    return (
      <input
        type="text"
        value={value.join(" | ")}
        onChange={(e) =>
          onChange(
            e.target.value
              .split("|")
              .map((part) => part.trim())
              .filter(Boolean)
          )
        }
      />
    );
  }
  return <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function clone(value) {
  try {
    return value ? JSON.parse(JSON.stringify(value)) : null;
  } catch {
    return null;
  }
}
